import { useEffect, useRef } from 'react'
import { client } from '@Utils'
import { simulationEndpoints } from '@URLS'

/**
 * Custom hook that debounces simulation state changes and auto-saves
 * to the backend.  Only active when activityPk is set (activity mode).
 */
export function useSaveProgress(state, chainState, activityPk, coursenum) {
  const timerRef = useRef(null)
  const lastSavedRef = useRef(null)

  useEffect(() => {
    if (!activityPk) return

    if (timerRef.current) clearTimeout(timerRef.current)

    timerRef.current = setTimeout(() => {
      const payload = buildSavePayload(state, chainState)
      const payloadStr = JSON.stringify(payload)
      if (payloadStr === lastSavedRef.current) return // No change
      lastSavedRef.current = payloadStr

      client
        .post(simulationEndpoints.saveProgress({ coursenum, activityPk }), payload)
        .catch(err => console.error('Auto-save failed:', err))
    }, 2000)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [state, chainState, activityPk, coursenum])
}

export function buildSavePayload(state, chainState) {
  const taskResults = []

  // Extract task completion data from single simulation state
  if (state && state.tasks) {
    for (const task of state.tasks) {
      taskResults.push({
        simulationGuid: state.guid || '',
        taskGuid: task.guid || task.id || '',
        taskTitle: task.title || '',
        completed: task.status === 'completed',
        pointsPossible: task.points || 0,
        pointsEarned: task.status === 'completed' ? (task.points || 0) : 0,
        resultData: task.progress || {},
      })
    }
  }

  // For chains, build task results in a single pass over all simulations
  if (chainState && chainState.simulations) {
    taskResults.length = 0
    for (let i = 0; i < chainState.simulations.length; i++) {
      const sim = chainState.simulations[i]
      if (sim.status !== 'completed' && sim.status !== 'active') continue

      const isActive = i === chainState.activeIndex
      const simGuid = sim.guid || ''

      // Pick a task source that carries real per-task status, so we never award
      // credit for objectives the student didn't meet:
      //   - active sim: live state.tasks (falling back to its snapshot)
      //   - completed sim: completedTasks / stateSnapshot.tasks (both have status)
      // Only when no status-bearing source exists do we fall back to the config
      // task list (which has no status) and treat the whole sim as completed.
      let tasks
      let hasStatus
      if (isActive) {
        tasks = (state && state.tasks && state.tasks.length > 0)
          ? state.tasks
          : (sim.stateSnapshot && sim.stateSnapshot.tasks) || sim.tasks || []
        hasStatus = true
      } else {
        const snapshotTasks = sim.completedTasks || (sim.stateSnapshot && sim.stateSnapshot.tasks)
        if (snapshotTasks && snapshotTasks.length > 0) {
          tasks = snapshotTasks
          hasStatus = true
        } else {
          tasks = sim.tasks || []
          hasStatus = false
        }
      }

      for (const task of tasks) {
        const completed = hasStatus ? task.status === 'completed' : true
        taskResults.push({
          simulationGuid: simGuid,
          taskGuid: task.guid || task.id || '',
          taskTitle: task.title || '',
          completed,
          pointsPossible: task.points || 0,
          pointsEarned: completed ? (task.points || 0) : 0,
          resultData: task.progress || {},
        })
      }
    }
  }

  return { state, chainState: chainState || {}, taskResults }
}
